import { useState, useCallback, createContext, useContext } from 'react';

const WATCHLIST_KEY = 'market_watch_watchlist';

// Load initial watchlist from localStorage
const loadInitialWatchlist = () => {
  try {
    const stored = localStorage.getItem(WATCHLIST_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      return Array.isArray(parsed) ? parsed : [];
    }
  } catch (error) {
    console.error('Error loading watchlist:', error);
  }
  return [];
};

// Context for sharing watchlist state across components
const WatchlistContext = createContext(null);

export const useWatchlist = () => {
  const context = useContext(WatchlistContext);
  if (!context) {
    throw new Error('useWatchlist must be used within a WatchlistProvider');
  }
  return context;
};

export const WatchlistProvider = ({ children }) => {
  // Initialize state from localStorage (only runs once)
  const [watchlist, setWatchlist] = useState(loadInitialWatchlist);

  // Save to localStorage helper
  const saveToStorage = useCallback((newWatchlist) => {
    try {
      localStorage.setItem(WATCHLIST_KEY, JSON.stringify(newWatchlist));
    } catch (error) {
      console.error('Error saving watchlist:', error);
    }
  }, []);

  // Add a stock to watchlist
  const addToWatchlist = useCallback((symbol) => {
    setWatchlist(prev => {
      if (prev.includes(symbol)) return prev;
      const newList = [...prev, symbol];
      saveToStorage(newList);
      return newList;
    });
  }, [saveToStorage]);

  // Remove a stock from watchlist
  const removeFromWatchlist = useCallback((symbol) => {
    setWatchlist(prev => {
      const newList = prev.filter(s => s !== symbol);
      saveToStorage(newList);
      return newList;
    });
  }, [saveToStorage]);

  // Toggle a stock in watchlist
  const toggleWatchlist = useCallback((symbol) => {
    setWatchlist(prev => {
      let newList;
      if (prev.includes(symbol)) {
        newList = prev.filter(s => s !== symbol);
      } else {
        newList = [...prev, symbol];
      }
      saveToStorage(newList);
      return newList;
    });
  }, [saveToStorage]);

  // Check if a stock is in watchlist
  const isWatched = useCallback((symbol) => {
    return watchlist.includes(symbol);
  }, [watchlist]);

  // Clear entire watchlist
  const clearWatchlist = useCallback(() => {
    setWatchlist([]);
    saveToStorage([]);
  }, [saveToStorage]);

  const value = {
    watchlist,
    watchlistCount: watchlist.length,
    isLoaded: true,
    addToWatchlist,
    removeFromWatchlist,
    toggleWatchlist,
    isWatched,
    clearWatchlist,
  };

  return (
    <WatchlistContext.Provider value={value}>
      {children}
    </WatchlistContext.Provider>
  );
};

export default WatchlistProvider;
